import React, { Component } from 'react';
import classNames from 'classnames';
import PropTypes from 'prop-types';
import Animate from 'rc-animate';
import _ from 'lodash';
import i18n from 'react-intl-universal';

import styles from './Modal.scss';

import keyCode from '../../constants/keyCode';
import Icon from '../../components/Icon/Icon';
import LazyRenderBox from './LazyRenderBox';
import Portal from '../../utils/Portal';

export default class Modal extends Component {
  static propTypes = {
    getContainer: PropTypes.func,
    visible: PropTypes.bool,
    isMiddle: PropTypes.bool, // 是否垂直居中
    showMask: PropTypes.bool, // 是否显示蒙层
    maskClosable: PropTypes.bool, // 点击蒙层是否关闭modal
    closable: PropTypes.bool, // 是否显示右上角的关闭按钮
    showFooter: PropTypes.bool, // 是否显示footer
    tips: PropTypes.bool, // 是否显示title中的提示
    title: PropTypes.oneOfType([PropTypes.string, PropTypes.node]),
    width: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
    top: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
    modalClassName: PropTypes.string,
    maskClassName: PropTypes.string,
    bodyClass: PropTypes.string,
    footerClass: PropTypes.string,
    modalStyle: PropTypes.object,
    maskStyle: PropTypes.object,
    bodyStyle: PropTypes.object,
    headerStyle: PropTypes.object,
    footerStyle: PropTypes.object,
    okText: PropTypes.string,
    cancelText: PropTypes.string,
    onOk: PropTypes.func,
    onCancel: PropTypes.func,
    afterClose: PropTypes.func,
  };

  static defaultProps = {
    visible: false,
    isMiddle: false,
    showMask: true,
    maskClosable: true,
    closable: true,
    showFooter: true,
    tips: true,
    width: 520,
    onOk: () => undefined,
    onCancel: () => undefined,
    afterClose: () => undefined,
    maskStyle: {},
    modalStyle: {},
    bodyStyle: {},
    headerStyle: {},
    footerStyle: {},
  };

  constructor(props) {
    super(props);
    this.state = {};
  }

  componentDidUpdate(prevProps) {
    const { visible } = this.props;
    // 打开modal
    if (visible) {
      // 只有在切换modal的visible时，才去focus
      if (!prevProps.visible) {
        if (this.wrap) {
          this.wrap.focus();
        } else {
          // 第一次打开
          setTimeout(() => {
            this.wrap.focus();
          }, 0);
        }
      }
    }
  }

  saveRef = name => (node) => {
    this[name] = node;
  };

  handleOk = (e) => {
    const { onOk } = this.props;
    if (_.isFunction(onOk)) {
      onOk(e);
    }
  };

  handleCancel = (e) => {
    const { onCancel } = this.props;
    if (_.isFunction(onCancel)) {
      onCancel(e);
    }
  };

  onWrapClick = (e) => {
    const { maskClosable } = this.props;
    if (e.target === e.currentTarget) {
      if (maskClosable) {
        this.handleCancel(e);
      }
    }
  };

  onWrapKeyDown = (e) => {
    if (e.keyCode === keyCode.ESC) {
      this.handleCancel(e);
    }
  };

  onModalLeave = () => {
    // 需要在动画结束时设定wrap的display，否则结束动画不触发
    const { afterClose } = this.props;
    if (this.wrap) {
      this.wrap.style.display = 'none';
    }
    if (_.isFunction(afterClose)) {
      afterClose();
    }
  };

  getWrapStyle = () => {
    // 在modal打开时，设定display
    // 在modal关闭时，不去设定，而是等待动画结束后设定
    const { visible } = this.props;
    if (visible) {
      return {
        display: null,
      };
    }
    return {};
  };

  getModalStyle = () => {
    const { isMiddle, modalStyle, width, top } = this.props;
    if (isMiddle) {
      return Object.assign({}, { width, top }, modalStyle);
    }
    return Object.assign({}, { width, top: top || 120 }, modalStyle);
  };

  getButtonText = () => {
    const { okText, cancelText } = this.props;
    return {
      ok: okText || i18n.get('modal.confirm'),
      cancel: cancelText || i18n.get('modal.cancel'),
    };
  };

  getContainer = () => {
    if (_.isFunction(this.props.getContainer)) {
      return this.props.getContainer();
    }
    const container = document.createElement('div');
    document.body.appendChild(container);
    return container;
  };

  getMaskElement = () => {
    const { visible, showMask, maskClassName, maskStyle } = this.props;
    if (!showMask) {
      return null;
    }
    const maskClass = classNames(maskClassName, {
      [`${styles['modal-mask']}`]: true,
    });

    return (
      <Animate
        key="mask"
        showProp="visible"
        transitionAppear
        component=""
        transitionName="fade"
      >
        <LazyRenderBox
          key="mask"
          className={maskClass}
          hiddenClassName={styles['modal-mask-hidden']}
          visible={visible}
          style={maskStyle}
        />
      </Animate>
    );
  };

  getModalElement = () => {
    // eslint-disable-next-line max-len
    const { visible, closable, title, showFooter, isMiddle, modalClassName, bodyClass, footerClass, bodyStyle, headerStyle, footerStyle, tips } = this.props;
    const btnText = this.getButtonText();
    const modalClass = classNames(modalClassName, {
      [`${styles['modal-entity']}`]: true,
      [`${styles['modal-entity-middle']}`]: !!isMiddle,
    });
    const tip = (<span className={styles['modal-header-tip']}>{i18n.get('phoneModal.tip')}？<a href="https://www.jianshu.com/p/794b92192f62" target="_blank" rel="noopener noreferrer"> {i18n.get('phoneModal.href')}</a></span>);
    const header = title ? (
      <div className={styles['modal-header']} style={headerStyle}>
        <div className={styles['modal-header-title']}>
          {title} {tips && tip}
        </div>
      </div>
    ) : null;

    const footer = showFooter ? (
      <div className={classNames(footerClass, styles['modal-footer'])} style={footerStyle}>
        <Modal.Button onClick={this.handleCancel}>{btnText.cancel}</Modal.Button>
        <Modal.Button onClick={this.handleOk} type={'primary'}>{btnText.ok}</Modal.Button>
      </div>
    ) : null;

    const closer = closable ? (
      <div
        className={styles['modal-closer']}
        aria-label="Close"
        onClick={this.handleCancel}
      >
        <Icon name={'close'}/>
      </div>
    ) : null;

    const element = (
      <div className={styles['modal-content']}>
        {closer}
        {header}
        <div className={classNames(bodyClass, styles['modal-body'])} style={bodyStyle}>
          {this.props.children}
        </div>
        {footer}
      </div>
    );

    return (
      <Animate
        key="modal-element"
        showProp="visible"
        onLeave={this.onModalLeave}
        transitionName={'move-up'}
        component=""
        transitionAppear
      >
        <LazyRenderBox
          key="modal-element"
          role="document"
          ref={this.saveRef('modal')}
          className={modalClass}
          style={this.getModalStyle()}
          visible={visible}
        >
          {element}
        </LazyRenderBox>
      </Animate>
    );
  };

  render() {
    const { visible } = this.props;

    if (visible || this.container) {
      return (
        <Portal getContainer={this.getContainer}>
          <div
            className={styles['modal-container']}
            ref={this.saveRef('container')}
          >
            {this.getMaskElement()}
            <div
              role="dialog"
              tabIndex={-1}
              className={styles['modal-wrap']}
              style={this.getWrapStyle()}
              ref={this.saveRef('wrap')}
              onKeyDown={this.onWrapKeyDown}
              onClick={this.onWrapClick}
            >
              {this.getModalElement()}
            </div>
          </div>
        </Portal>
      );
    }
    return null;
  }
}



// WEBPACK FOOTER //
// ./src/components/Modal/Modal.js